/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Super Admin Authentication Store (Task 11.1.3 & Sprint 12 Token Lifecycle)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { create } from 'zustand';

export interface AdminUser {
  id: string;
  email: string;
  fullName: string;
  role: 'super_admin';
  avatarUrl?: string;
}

export interface AdminAuthState {
  token: string | null;
  refreshToken: string | null;
  adminUser: AdminUser | null;
  rememberMe: boolean;
  isAuthenticated: boolean;
  isLoading: boolean;
  setSession: (
    token: string,
    user: AdminUser,
    refreshToken?: string | null,
    rememberMe?: boolean
  ) => void;
  updateAdminAccessToken: (token: string, newRefreshToken?: string | null) => void;
  logout: () => void;
  checkSession: () => void;
}

export const ADMIN_AUTH_TOKEN_KEY = 'sophia_admin_access_token';
export const LEGACY_ADMIN_TOKEN_KEY = 'sophia_admin_token';
export const ADMIN_REFRESH_TOKEN_KEY = 'sophia_admin_refresh_token';
export const ADMIN_USER_STORAGE_KEY = 'sophia_admin_user_profile';
export const ADMIN_REMEMBER_KEY = 'sophia_admin_remember_me';

function getLocal(): Storage | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null;
  } catch {
    return null;
  }
}

function getSession(): Storage | null {
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage : null;
  } catch {
    return null;
  }
}

const getInitialRememberMe = (): boolean => {
  const local = getLocal();
  const session = getSession();
  if (!local && !session) return true;

  const rem = local?.getItem(ADMIN_REMEMBER_KEY);
  if (rem !== null && rem !== undefined) return rem === 'true';
  if (session?.getItem(ADMIN_AUTH_TOKEN_KEY) && !local?.getItem(ADMIN_AUTH_TOKEN_KEY)) {
    return false;
  }
  return true;
};

const getInitialToken = (remember: boolean): string | null => {
  const local = getLocal();
  const session = getSession();
  if (remember) {
    return (
      local?.getItem(ADMIN_AUTH_TOKEN_KEY) ||
      local?.getItem(LEGACY_ADMIN_TOKEN_KEY) ||
      null
    );
  }
  return (
    session?.getItem(ADMIN_AUTH_TOKEN_KEY) ||
    local?.getItem(ADMIN_AUTH_TOKEN_KEY) ||
    local?.getItem(LEGACY_ADMIN_TOKEN_KEY) ||
    null
  );
};

const getInitialRefreshToken = (remember: boolean): string | null => {
  if (!remember) return null;
  return getLocal()?.getItem(ADMIN_REFRESH_TOKEN_KEY) ?? null;
};

const getInitialUser = (remember: boolean): AdminUser | null => {
  const local = getLocal();
  const session = getSession();
  const raw = remember
    ? local?.getItem(ADMIN_USER_STORAGE_KEY)
    : session?.getItem(ADMIN_USER_STORAGE_KEY) ?? local?.getItem(ADMIN_USER_STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const initialRemember = getInitialRememberMe();
const initialToken = getInitialToken(initialRemember);
const initialRefreshToken = getInitialRefreshToken(initialRemember);
const initialUser = getInitialUser(initialRemember);

export const useAdminAuthStore = create<AdminAuthState>((set, get) => ({
  token: initialToken,
  refreshToken: initialRefreshToken,
  adminUser: initialUser,
  rememberMe: initialRemember,
  isAuthenticated: Boolean(initialToken),
  isLoading: false,

  setSession: (
    token: string,
    user: AdminUser,
    refreshToken?: string | null,
    rememberMe = true
  ) => {
    const local = getLocal();
    const session = getSession();

    if (rememberMe) {
      local?.setItem(ADMIN_AUTH_TOKEN_KEY, token);
      local?.setItem(LEGACY_ADMIN_TOKEN_KEY, token);
      local?.setItem(ADMIN_USER_STORAGE_KEY, JSON.stringify(user));
      if (refreshToken) {
        local?.setItem(ADMIN_REFRESH_TOKEN_KEY, refreshToken);
      }
      local?.setItem(ADMIN_REMEMBER_KEY, 'true');

      // Purge ephemeral session
      session?.removeItem(ADMIN_AUTH_TOKEN_KEY);
      session?.removeItem(ADMIN_USER_STORAGE_KEY);
      session?.removeItem(ADMIN_REFRESH_TOKEN_KEY);
      session?.removeItem(ADMIN_REMEMBER_KEY);
    } else {
      session?.setItem(ADMIN_AUTH_TOKEN_KEY, token);
      session?.setItem(ADMIN_USER_STORAGE_KEY, JSON.stringify(user));
      session?.setItem(ADMIN_REMEMBER_KEY, 'false');

      // Purge persistent storage for shared computers
      local?.removeItem(ADMIN_AUTH_TOKEN_KEY);
      local?.removeItem(LEGACY_ADMIN_TOKEN_KEY);
      local?.removeItem(ADMIN_REFRESH_TOKEN_KEY);
      local?.removeItem(ADMIN_USER_STORAGE_KEY);
      local?.removeItem(ADMIN_REMEMBER_KEY);
    }

    set({
      token,
      refreshToken: rememberMe ? (refreshToken ?? null) : null,
      adminUser: user,
      rememberMe,
      isAuthenticated: true,
      isLoading: false,
    });
  },

  updateAdminAccessToken: (token: string, newRefreshToken?: string | null) => {
    const rem = get().rememberMe;
    const local = getLocal();
    const session = getSession();

    if (rem) {
      local?.setItem(ADMIN_AUTH_TOKEN_KEY, token);
      local?.setItem(LEGACY_ADMIN_TOKEN_KEY, token);
      if (newRefreshToken) {
        local?.setItem(ADMIN_REFRESH_TOKEN_KEY, newRefreshToken);
      }
    } else {
      session?.setItem(ADMIN_AUTH_TOKEN_KEY, token);
    }

    set((state) => ({
      token,
      refreshToken:
        get().rememberMe && newRefreshToken !== undefined ? newRefreshToken : state.refreshToken,
      isAuthenticated: true,
    }));
  },

  logout: () => {
    const local = getLocal();
    const session = getSession();

    local?.removeItem(ADMIN_AUTH_TOKEN_KEY);
    local?.removeItem(LEGACY_ADMIN_TOKEN_KEY);
    local?.removeItem(ADMIN_REFRESH_TOKEN_KEY);
    local?.removeItem(ADMIN_USER_STORAGE_KEY);
    local?.removeItem(ADMIN_REMEMBER_KEY);

    session?.removeItem(ADMIN_AUTH_TOKEN_KEY);
    session?.removeItem(ADMIN_USER_STORAGE_KEY);
    session?.removeItem(ADMIN_REFRESH_TOKEN_KEY);
    session?.removeItem(ADMIN_REMEMBER_KEY);

    set({
      token: null,
      refreshToken: null,
      adminUser: null,
      isAuthenticated: false,
      isLoading: false,
    });
  },

  checkSession: () => {
    const rem = getInitialRememberMe();
    const token = getInitialToken(rem);
    const user = getInitialUser(rem);
    const refToken = getInitialRefreshToken(rem);

    if (token && user) {
      set({
        token,
        refreshToken: refToken,
        adminUser: user,
        rememberMe: rem,
        isAuthenticated: true,
      });
    } else {
      set({
        token: null,
        refreshToken: null,
        adminUser: null,
        isAuthenticated: false,
      });
    }
  },
}));

// Listen to global 401 unauthorized events to force sign-out
if (typeof window !== 'undefined') {
  window.addEventListener('sophia:admin-unauthorized', () => {
    useAdminAuthStore.getState().logout();
  });
}
